import { uniqueMembers } from './markushSummary.ts';
import type { DrawingDocument, NormalizedPoint, Point2D } from '../model';
import type { MarkushDrawingDocument, RGroupDefinition, VariablePosition } from './markushDocument.ts';
import { isPlaceholderPosition, isSingleRing } from './markushDocument.ts';
import { assignAtomList, assignRGroup, clearVariablePosition, updateScaffold } from './markushMutations.ts';
import {
  drawingDocumentToMarkushStructure,
  markushPositionMarkers,
  markushStructureToDrawingDocument
} from './markushProjections.ts';
import { markushValidationIssues, type MarkushIssueCode } from './markushValidation.ts';
import { createId, withoutKey } from './drawingDocumentCore.ts';
import { addAtom, deleteAtom } from './graphTopologyMutations.ts';
import { canvasRings } from './objectEditing.ts';
import { documentToAtoms, documentToLines, type DrawingAtom } from './drawingDocumentProjections.ts';

// #648 (M3a): the workspace's Markush mode keeps editing an ordinary v10 DrawingDocument -- a
// placeholder stays a real atom there, so every existing tool (select, move, delete, bonds)
// works on it unchanged -- and carries the Markush-only data alongside it as MarkushLayers, in
// the same undo entry. The MarkushDrawingDocument is derived on demand: the placeholders' atom
// entries are stripped, which is exactly M2's scaffold shape. Every rule below delegates to M2's
// mutations on that derived document rather than re-implementing them.

export interface MarkushLayers {
  readonly rGroups: Readonly<Record<string, RGroupDefinition>>;
  readonly variablePositions: readonly VariablePosition[];
}

export const EMPTY_MARKUSH_LAYERS: MarkushLayers = { rGroups: {}, variablePositions: [] };

/** Ids of the live atoms that stand for a placeholder position (R-group, atom list, ...). */
export function placeholderAtomIds(layers: MarkushLayers): ReadonlySet<string> {
  return new Set(layers.variablePositions.filter(isPlaceholderPosition).map((position) => position.atomId));
}

export function toMarkushDrawingDocument(document: DrawingDocument, layers: MarkushLayers): MarkushDrawingDocument {
  const structure = drawingDocumentToMarkushStructure(document);
  const placeholders = placeholderAtomIds(layers);
  let atoms = structure.atoms;
  let formalCharges = structure.formalCharges;
  for (const atomId of placeholders) {
    atoms = withoutKey(atoms, atomId);
    formalCharges = withoutKey(formalCharges, atomId);
  }
  return {
    schemaVersion: 'chem-drawing.v11',
    kind: 'markush',
    scaffold: { ...structure, atoms, formalCharges },
    rGroups: layers.rGroups,
    variablePositions: layers.variablePositions,
    ...(document.page ? { page: document.page } : {})
  };
}

function layersOf(document: MarkushDrawingDocument): MarkushLayers {
  return { rGroups: document.rGroups, variablePositions: document.variablePositions };
}

/**
 * Carries layers across an edit made by any existing tool (`previous` -> `next`). Positions whose
 * atoms were deleted are pruned exactly as M2's updateScaffold prunes them, and a placeholder
 * whose live atom was retyped (the element tool on an R atom) loses its position -- the user
 * replaced the R with a concrete element. Returns `layers` itself when nothing changed.
 */
export function reconcileMarkushLayers(
  layers: MarkushLayers,
  previous: DrawingDocument,
  next: DrawingDocument
): MarkushLayers {
  if (layers.variablePositions.length === 0) return layers;
  const retyped = [...placeholderAtomIds(layers)].filter((atomId) => {
    const before = previous.structure.atoms[atomId];
    const after = next.structure.atoms[atomId];
    return before && after && before.atomicNumber !== after.atomicNumber;
  });
  let markush = toMarkushDrawingDocument(next, layers);
  for (const atomId of retyped) {
    markush = { ...markush, variablePositions: markush.variablePositions.filter((p) => !isPlaceholderAt(p, atomId)) };
  }
  const pruned = updateScaffold(markush, (scaffold) => scaffold);
  const unchanged =
    pruned.variablePositions.length === layers.variablePositions.length &&
    pruned.variablePositions.every((position, index) => position === layers.variablePositions[index]);
  return unchanged ? layers : layersOf(pruned);
}

function isPlaceholderAt(position: VariablePosition, atomId: string): boolean {
  return isPlaceholderPosition(position) && position.atomId === atomId;
}

function bondCount(document: DrawingDocument, atomId: string): number {
  return document.structure.graph.edges.filter((edge) => edge.source === atomId || edge.target === atomId).length;
}

/** A Markush-mode edit: the live drawing and its layers together. Unchanged inputs come back as
 *  the same objects, so callers can tell a no-op by identity. */
export interface MarkushEdit {
  readonly document: DrawingDocument;
  readonly layers: MarkushLayers;
}

/**
 * The R-group label tool's click on an atom (#648). A terminal atom (exactly one bond --
 * confirmed M2 rule 1) becomes an R-group position labelled `label`; clicking an R atom that
 * already carries `label` clears it again (a toggle, like re-clicking an armed element). An
 * attachment-point label (#650) relabels the same way, and its toggle-off also removes the
 * bond-less placeholder atom it lives on. Anything else comes back unchanged -- a non-terminal
 * atom, an unknown atom, or one M2 refuses (inside a repeat unit or behind an attachment point).
 */
export function applyRGroupLabel(
  document: DrawingDocument,
  layers: MarkushLayers,
  atomId: string,
  label: string
): MarkushEdit {
  const unchanged = { document, layers };
  if (!document.structure.atoms[atomId]) return unchanged;
  const existing = layers.variablePositions.find((position) => isPlaceholderAt(position, atomId));
  if (existing?.kind !== 'attachment-point' && bondCount(document, atomId) !== 1) return unchanged;
  const markush = toMarkushDrawingDocument(document, layers);
  if ((existing?.kind === 'r-group' || existing?.kind === 'attachment-point') && existing.rGroupLabel === label) {
    const cleared = layersOf(clearVariablePosition(markush, atomId));
    return {
      document: existing.kind === 'attachment-point' ? deleteAtom(document, atomId) : document,
      layers: cleared
    };
  }
  const assigned = assignRGroup(markush, atomId, label);
  return assigned === markush ? unchanged : { document, layers: layersOf(assigned) };
}

/** #654: the armed atom-list tool's name for a set of elements -- `atom-list-6,7,8,16` (sorted,
 *  deduplicated, so the same list is always the same tool). */
export function atomListTool(atomicNumbers: readonly number[]): `atom-list-${string}` {
  return `atom-list-${[...new Set(atomicNumbers)].sort((a, b) => a - b).join(',')}`;
}

/** The element list an `atom-list-…` tool carries; undefined for any other tool. */
export function atomListFromTool(tool: string): readonly number[] | undefined {
  if (!tool.startsWith('atom-list-')) return undefined;
  const numbers = tool.slice('atom-list-'.length).split(',').map(Number);
  return numbers.every((z) => Number.isInteger(z) && z >= 1 && z <= 118) ? numbers : undefined;
}

/**
 * #654: the atom-list tool's click. Any atom -- a backbone position keeps every bond -- becomes a
 * variable heteroatom allowing `allowed` (two or more elements; one element is just that element).
 * Clicking an atom that already has the same list clears it (the toggle every armed tool has); a
 * different list replaces it, as does clicking an R atom. Unchanged for an unknown atom, a short
 * list, an attachment-point label (bond-less; an element list there means nothing), or an atom M2
 * locks (behind an attachment point, inside a repeat unit). The live atom keeps its element.
 */
export function applyAtomList(
  document: DrawingDocument,
  layers: MarkushLayers,
  atomId: string,
  allowed: readonly number[]
): MarkushEdit {
  const unchanged = { document, layers };
  const list = [...new Set(allowed)].sort((a, b) => a - b);
  if (!document.structure.atoms[atomId] || list.length < 2) return unchanged;
  const markush = toMarkushDrawingDocument(document, layers);
  const existing = layers.variablePositions.find((position) => isPlaceholderAt(position, atomId));
  if (existing?.kind === 'atom-list' && existing.allowedAtomicNumbers.join(',') === list.join(',')) {
    return { document, layers: layersOf(clearVariablePosition(markush, atomId)) };
  }
  const assigned = assignAtomList(markush, atomId, list);
  return assigned === markush ? unchanged : { document, layers: layersOf(assigned) };
}

function toCoordinate(document: DrawingDocument, point: NormalizedPoint): Point2D {
  const { width, height } = document.depiction.coordinateSet;
  return { x: point.x * width, y: point.y * height };
}

function polygonArea(points: readonly Point2D[]): number {
  let twiceArea = 0;
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length]!;
    twiceArea += point.x * next.y - next.x * point.y;
  });
  return Math.abs(twiceArea) / 2;
}

function containsPoint(points: readonly Point2D[], point: Point2D): boolean {
  let inside = false;
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index, index += 1) {
    const a = points[index]!;
    const b = points[previous]!;
    if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * #650: the ring whose interior contains `point` (document-normalized), as its atom ids in ring
 * order -- the smallest such ring, so a click in one half of naphthalene picks that half, never
 * the fused pair (single-ring attachment points only in v1). Undefined outside every ring.
 */
export function ringAtPoint(document: DrawingDocument, point: NormalizedPoint): readonly string[] | undefined {
  const coordinates = document.depiction.coordinateSet.coordinates;
  const endpoints = Object.fromEntries(
    document.structure.graph.edges.map((edge) => [edge.id, [edge.source, edge.target] as const])
  );
  const target = toCoordinate(document, point);
  let best: { readonly atomIds: readonly string[]; readonly area: number } | undefined;
  for (const ring of canvasRings(documentToAtoms(document), documentToLines(document), endpoints)) {
    const polygon = ring.atomIds.map((id) => coordinates[id]);
    if (polygon.some((corner) => !corner)) continue;
    const corners = polygon as Point2D[];
    if (!containsPoint(corners, target)) continue;
    const area = polygonArea(corners);
    if (!best || area < best.area) best = { atomIds: ring.atomIds, area };
  }
  return best?.atomIds;
}

/** How far an attachment label sits beyond the ring bond its line crosses, in ring-bond lengths --
 *  far enough that it can't be mistaken for a substituent on a neighbouring ring atom. */
const ATTACHMENT_OVERHANG = 1.25;
/** Ring-bond exits this close to equally level (about 6°) count as a tie, broken by clearance. */
const TILT_TIE_RADIANS = 0.1;

/**
 * #650: the R-group tool's click inside a ring. Adds a bond-less placeholder atom outside the
 * ring -- on the clicked side, through the ring bond nearest horizontal (see below) -- and an
 * `attachment-point` position for `label` over that ring.
 * Unchanged when the click isn't inside a ring of concrete (non-placeholder) atoms.
 */
export function addRingAttachment(
  document: DrawingDocument,
  layers: MarkushLayers,
  point: NormalizedPoint,
  label: string
): MarkushEdit {
  const unchanged = { document, layers };
  const rGroupLabel = label.trim();
  const ringAtomIds = ringAtPoint(document, point);
  const placeholders = placeholderAtomIds(layers);
  if (!rGroupLabel || !ringAtomIds || ringAtomIds.some((id) => placeholders.has(id))) return unchanged;
  if (!isSingleRing(document.structure.graph, ringAtomIds)) return unchanged;

  const coordinates = document.depiction.coordinateSet.coordinates;
  const ring = ringAtomIds.map((id) => coordinates[id]!);
  const centre = {
    x: ring.reduce((sum, corner) => sum + corner.x, 0) / ring.length,
    y: ring.reduce((sum, corner) => sum + corner.y, 0) / ring.length
  };
  const bondLength =
    ring.reduce((sum, corner, index) => {
      const next = ring[(index + 1) % ring.length]!;
      return sum + Math.hypot(next.x - corner.x, next.y - corner.y);
    }, 0) / ring.length;
  // Owner feedback on #650: a line aimed wherever the click was could run straight through a ring
  // atom and read as a bond *to that atom*. Instead the line leaves horizontally-ish on the clicked
  // side (right for a click on the centre), always through the *middle of a ring bond* -- the one
  // whose midpoint direction is closest to horizontal -- so it can never pass through an atom, and
  // the label sits well clear of the ring: ATTACHMENT_OVERHANG ring-bond lengths past that bond.
  const click = toCoordinate(document, point);
  const side = click.x < centre.x ? -1 : 1;
  const midpoints = ring.map((corner, index) => {
    const next = ring[(index + 1) % ring.length]!;
    return { x: (corner.x + next.x) / 2, y: (corner.y + next.y) / 2 };
  });
  const candidates = midpoints.map((midpoint) => {
    const dx = midpoint.x - centre.x;
    const dy = midpoint.y - centre.y;
    const distance = Math.hypot(dx, dy);
    const direction = { x: dx / distance, y: dy / distance };
    const reach = distance + ATTACHMENT_OVERHANG * bondLength;
    const spot = { x: centre.x + direction.x * reach, y: centre.y + direction.y * reach };
    // Angle from horizontal, on the clicked side; a midpoint on the other side scores worst.
    const tilt = dx * side > 0 ? Math.abs(Math.atan2(dy, Math.abs(dx))) : Math.PI;
    // Room around the label spot -- breaks a tie between two equally level bonds (a ring with an
    // atom at its side point) toward the one away from existing substituents.
    const clearance = Math.min(
      Infinity,
      ...Object.entries(coordinates)
        .filter(([id]) => !ringAtomIds.includes(id))
        .map(([, other]) => Math.hypot(other.x - spot.x, other.y - spot.y))
    );
    return { spot, tilt, clearance };
  });
  const levelest = Math.min(...candidates.map((candidate) => candidate.tilt));
  const exit = candidates
    .filter((candidate) => candidate.tilt <= levelest + TILT_TIE_RADIANS)
    .reduce((best, candidate) => (candidate.clearance > best.clearance ? candidate : best));
  const { width, height } = document.depiction.coordinateSet;
  const labelPoint = { x: exit.spot.x / width, y: exit.spot.y / height };

  const before = new Set(document.structure.graph.nodes.map((node) => node.id));
  const withPlaceholder = addAtom(document, labelPoint, 6);
  const atomId = withPlaceholder.structure.graph.nodes.find((node) => !before.has(node.id))?.id;
  if (!atomId) return unchanged;
  return {
    document: withPlaceholder,
    layers: {
      ...layers,
      variablePositions: [
        ...layers.variablePositions,
        { kind: 'attachment-point', atomId, ringAtomIds: [...ringAtomIds], rGroupLabel }
      ]
    }
  };
}

/**
 * #650, confirmed M2 rule 4: an attachment point whose ring lost an atom goes *together with its
 * placeholder*. Layer derivation already drops the position; this removes the now-meaningless
 * bond-less placeholder atom from `next` in the same edit, so no stray atom is left behind.
 */
export function removeOrphanedAttachmentAtoms(
  layers: MarkushLayers,
  previous: DrawingDocument,
  next: DrawingDocument
): DrawingDocument {
  let cleaned = next;
  for (const position of layers.variablePositions) {
    if (position.kind !== 'attachment-point' || !next.structure.atoms[position.atomId]) continue;
    const lostRingAtom = position.ringAtomIds.some((id) => previous.structure.atoms[id] && !next.structure.atoms[id]);
    if (lostRingAtom) cleaned = deleteAtom(cleaned, position.atomId);
  }
  return cleaned;
}

export interface MarkushAttachmentLine {
  readonly id: string;
  readonly start: NormalizedPoint;
  readonly end: NormalizedPoint;
}

/** #650: one decorative line per attachment point, from its label to its ring's centre, in the
 *  same (already projected) space as `atoms`. Not a graph bond -- derived from the layers. */
export function markushAttachmentLines(
  atoms: readonly DrawingAtom[],
  layers: MarkushLayers
): readonly MarkushAttachmentLine[] {
  const positionById = new Map(atoms.map((atom) => [atom.id, atom.position]));
  return layers.variablePositions.flatMap((position) => {
    if (position.kind !== 'attachment-point') return [];
    const start = positionById.get(position.atomId);
    const ring = position.ringAtomIds.map((id) => positionById.get(id));
    if (!start || ring.some((corner) => !corner)) return [];
    const end = {
      x: ring.reduce((sum, corner) => sum + corner!.x, 0) / ring.length,
      y: ring.reduce((sum, corner) => sum + corner!.y, 0) / ring.length
    };
    return [{ id: position.atomId, start, end }];
  });
}

/** #650: atoms to highlight as invalid Markush positions (an R-group with the wrong bond count, a
 *  bonded attachment label, a broken ring, ...). An undefined label is a normal authoring state
 *  until M3c and is not highlighted. */
export function markushIssueAtomIds(document: DrawingDocument, layers: MarkushLayers): ReadonlySet<string> {
  if (layers.variablePositions.length === 0) return new Set();
  return new Set(
    markushValidationIssues(toMarkushDrawingDocument(document, layers)).flatMap((issue) =>
      issue.code !== 'undefined-r-group' && issue.atomId ? [issue.atomId] : []
    )
  );
}

/** Display text per R atom (`R1`, `[C,N,O,S]`, ...), keyed by atom id, for label rendering. */
export function markushAtomLabels(document: DrawingDocument, layers: MarkushLayers): Readonly<Record<string, string>> {
  if (layers.variablePositions.length === 0) return {};
  return Object.fromEntries(
    markushPositionMarkers(toMarkushDrawingDocument(document, layers)).map((marker) => [marker.atomId, marker.label])
  );
}

/** The drawing with every placeholder atom removed -- what formula/validation readouts should
 *  see, so an R atom is never counted as carbon. */
export function withoutPlaceholderAtoms(document: DrawingDocument, layers: MarkushLayers): DrawingDocument {
  let concrete = document;
  for (const atomId of placeholderAtomIds(layers)) concrete = deleteAtom(concrete, atomId);
  return concrete;
}

// #648 -- opening an exported Markush JSON file (Export JSON in Markush mode). The inverse of
// toMarkushDrawingDocument: each atom-less placeholder becomes a live carbon again, carrying its
// position in the layers. Issues that are a normal mid-authoring state (a label with no
// definition yet, an R atom whose bond was deleted) import fine; only a structurally broken
// document is refused.
const STRUCTURAL_ISSUES: ReadonlySet<MarkushIssueCode> = new Set([
  'malformed-graph',
  'missing-coordinate',
  'missing-formal-charge',
  'position-not-placeholder',
  'duplicate-position',
  'orphan-placeholder',
  'fragment-invalid'
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** True for anything claiming to be a v11 Markush drawing -- routes a JSON import here. */
export function isMarkushDrawingJson(value: unknown): boolean {
  return isRecord(value) && value.kind === 'markush';
}

export function fromMarkushDrawingDocument(value: unknown): {
  readonly document: DrawingDocument;
  readonly layers: MarkushLayers;
} {
  if (!isRecord(value) || value.schemaVersion !== 'chem-drawing.v11' || value.kind !== 'markush') {
    throw new Error('Not a chem-drawing.v11 Markush document.');
  }
  const { scaffold, rGroups, variablePositions } = value;
  const shapeOk =
    isRecord(scaffold) &&
    isRecord(scaffold.graph) &&
    Array.isArray(scaffold.graph.nodes) &&
    Array.isArray(scaffold.graph.edges) &&
    isRecord(scaffold.atoms) &&
    isRecord(scaffold.bonds) &&
    isRecord(scaffold.formalCharges) &&
    isRecord(scaffold.coordinateSet) &&
    typeof scaffold.coordinateSet.width === 'number' &&
    scaffold.coordinateSet.width > 0 &&
    typeof scaffold.coordinateSet.height === 'number' &&
    scaffold.coordinateSet.height > 0 &&
    isRecord(scaffold.coordinateSet.coordinates) &&
    isRecord(rGroups) &&
    Array.isArray(variablePositions);
  if (!shapeOk) throw new Error('The Markush document is missing its scaffold, rGroups or variablePositions.');

  const markush = value as unknown as MarkushDrawingDocument;
  const structural = markushValidationIssues(markush).filter((issue) => STRUCTURAL_ISSUES.has(issue.code));
  if (structural.length > 0) throw new Error(structural[0]!.message);

  // #839 -- a document saved with repeated members (possible before members were kept unique)
  // opens with each member once, in order.
  const uniqueRGroups = Object.fromEntries(
    Object.entries(markush.rGroups).map(([label, definition]) => [
      label,
      definition.kind === 'enumerated' ? { ...definition, members: uniqueMembers(definition.members) } : definition
    ])
  ) as MarkushDrawingDocument['rGroups'];
  const layers: MarkushLayers = { rGroups: uniqueRGroups, variablePositions: markush.variablePositions };
  const placeholders = placeholderAtomIds(layers);
  const { scaffold: structure } = markush;
  const atoms = { ...structure.atoms };
  const formalCharges = { ...structure.formalCharges };
  for (const atomId of placeholders) {
    atoms[atomId] = { atomicNumber: 6 };
    formalCharges[atomId] = 0;
  }
  const document = markushStructureToDrawingDocument({ ...structure, atoms, formalCharges }, createId('markush'));
  // #772 -- the page travels with the drawing.
  return { document: markush.page ? { ...document, page: markush.page } : document, layers };
}
